import _ from 'lodash'
import { all, call, put, takeEvery, takeLatest } from 'redux-saga/effects'

import lang from 'lang'
import { translateResponseError } from 'common/utils'
import { validateField, validateFields } from 'common/validator'
import { changeModal } from 'services/actions'
import emotionWordsService, {
  deleteEmotionWordService,
  patchEmotionWordService,
  postEmotionWordService,
} from 'services/emotion-words'

import {
  addFieldError,
  addFieldsErrors,
  clearFieldError,
  DELETE_EMOTION_WORD,
  resetEmotionWordForm,
  SUBMIT_EMOTION_WORD,
  VALIDATE_EMOTION_WORD_FIELD,
} from './actions'
import type { EmotionWordFormPayload } from './actions'

function prepareData(payload: EmotionWordFormPayload): Record<string, unknown> {
  const data: Record<string, unknown> = {
    name: payload.name.value,
    definition: payload.definition.value,
    mood_meter: payload.moodMeter.value,
    charter: payload.charter.value,
    energy_lower: payload.energyLower.value,
    energy_higher: payload.energyHigher.value,
    pleasantness_higher: payload.pleasantnessHigher.value,
    pleasantness_lower: payload.pleasantnessLower.value,
    quadrant: '',
  }
  if (payload.id && payload.id.value) {
    data.id = payload.id.value
  }
  return data
}

export function* submitEmotionWordSaga({
  payload,
}: {
  payload: EmotionWordFormPayload
}): Generator<unknown, void, unknown> {
  const errors = validateFields(
    payload as unknown as Parameters<typeof validateFields>[0],
  ) as Record<string, string | undefined>
  if (_.isEmpty(errors)) {
    const data = prepareData(payload)
    let emotionWordResult: { error?: unknown } = {}
    if (data.id) {
      emotionWordResult = (yield call(
        patchEmotionWordService.requestSaga as (arg: {
          payload: { data: Record<string, unknown>; urlParams: { id: string } }
        }) => Generator,
        { payload: { data, urlParams: { id: String(data.id) } } },
      )) as { error?: unknown }
    } else {
      emotionWordResult = (yield call(
        postEmotionWordService.requestSaga as (arg: {
          payload: { data: Record<string, unknown> }
        }) => Generator,
        { payload: { data } },
      )) as { error?: unknown }
    }
    if (emotionWordResult.error) {
      const status = _.get(emotionWordResult, 'error.response.status')
      if (status === 409) {
        yield put(
          addFieldError({
            field: 'base',
            error: lang.page.administration.emotionWords.emotionWordForm.nameAlreadyExistsError,
          }),
        )
      } else {
        yield put(
          addFieldError({
            field: 'base',
            error: translateResponseError(
              emotionWordResult.error as Parameters<typeof translateResponseError>[0],
            ),
          }),
        )
      }
    } else {
      yield put(changeModal({ isOpen: false }))
      yield put(resetEmotionWordForm())
      yield put(emotionWordsService.actions.request())
    }
  } else {
    yield put(addFieldsErrors(errors))
  }
}

export function* deleteEmotionWordSaga({
  payload: { emotionWordId },
}: {
  payload: { emotionWordId: string }
}): Generator<unknown, void, unknown> {
  const result = (yield call(
    deleteEmotionWordService.requestSaga as (arg: {
      payload: { urlParams: { id: string } }
    }) => Generator,
    { payload: { urlParams: { id: emotionWordId } } },
  )) as { error?: unknown }
  if (!result.error) {
    yield put(changeModal({ isOpen: false }))
    yield put(emotionWordsService.actions.request())
  }
}

export function* validateFieldSaga({
  payload: { field, value, validations },
}: {
  payload: { field: string; value: string | number | boolean; validations?: string }
}): Generator<unknown, void, unknown> {
  const error = validateField({ field, value, fieldValidations: validations ?? '' })
  const errorStr = typeof error === 'string' ? error : error != null ? String(error) : undefined
  if (errorStr) yield put(addFieldError({ field, error: errorStr }))
  else yield put(clearFieldError({ field }))
}

export default function* saga(): Generator<unknown, void, unknown> {
  yield all([
    // @ts-expect-error redux-saga string action type
    takeEvery(VALIDATE_EMOTION_WORD_FIELD, validateFieldSaga),
    // @ts-expect-error redux-saga string action type
    takeLatest(SUBMIT_EMOTION_WORD, submitEmotionWordSaga),
    // @ts-expect-error redux-saga string action type
    takeLatest(DELETE_EMOTION_WORD, deleteEmotionWordSaga),
  ])
}
